import {
	IsNotEmpty,
	ValidateNested,
	ValidateIf,
	IsOptional,
	MinLength,
	IsPhoneNumber,
	Matches,
	IsEmail,
	IsArray,
	IsUrl,
	IsNumber,
	IsPositive,
	ArrayMaxSize,
	ArrayMinSize,
	IsInt,
	Min,
} from 'class-validator';
import { Type } from 'class-transformer';
import { DietDto } from './diet.dto';
import { UserAnswerDto } from './user-answer.dto';
import {
	DietsEnum,
	GendersEnum,
	UserRolesEnum,
	AcceptanceOptionsEnum,
	EmptyAvatarVariantEnum,
	LocationPrivacyOptionsEnum,
	AccountsPrivacyOptionsEnum,
	EventAttendanceOptionsEnum,
	StripeConnectedAccountStatusEnum,
} from '../../enums';
import { AddInterestToUserDto, InterestToUserDto } from './interest.dto';
import { AchievementDto } from './achievement.dto';
import { UserToFollowerDto } from './user-to-follower.dto';
import { FileUploadDto, FileDto } from './file.dto';
import { EventDto } from './event.dto';
import { OtherGenderDto } from './gender.dto';
import { Base64AllowedMimeTypes, MaxBase64FileSize } from '../../decorators';
import {
	ALLOWED_SWEAT_MATCH_IMAGE_MIME_TYPES,
	ALLOWED_SWEAT_MATCH_VIDEO_MIME_TYPES,
	ALLOWED_USER_AVATAR_MIME_TYPES,
	ALLOWED_USER_BG_IMAGE_MIME_TYPES,
	NO_WHITESPACE_REGEXP,
} from '../../constants';
import { isEqual } from 'lodash-es';
import { StreamChatDto } from './stream-chat.dto';
import { LookingForOptionDto } from './looking-for-option.dto';
import { EventTicketDto } from './event-ticket.dto';
import { EventPriceDto } from './event-price.dto';
import { UserToMembershipDto } from './user-to-membership.dto';
import { CityDto } from './city.dto';
import { UserAddressDto, UpdateUserAddressDto } from './user-address.dto';
import { AdminToCommunityProfileDto } from './admin-to-community-profile.dto';
import { AdminPermissionsDto } from './subadmin-config.dto';
import { LeaderboardSnapshotDto } from './leaderboard-snapshot.dto';
import { UserToChallengeDto } from './user-to-challenge.dto';
import { IntakeFormUserDataResults } from './intake-form-settings.dto';

export class UserRoleDto {
	id: UserRolesEnum;
	name: string;
}

export class ClientMetadataDto {
	// feature flags
	hideProPlanOnUI?: boolean;
	hideBlastLimitsOnUI?: boolean;
	enableShapedAiEventSection?: boolean;
	allowLocationBasedNotifications?: boolean;
	isBioInMarkdown?: boolean;
	/** allow widget subheader editing via event edit form */
	showSubHeaderInput?: boolean;
	/**
	 * allow to use Unlisted price tier
	 * @deprecated
	 */
	showTierUnlistedType?: boolean;
	/**
	 * allow to use Unlisted price tier
	 * @deprecated
	 */
	showUnlistedTierType?: boolean;
	/**
	 * allow to use Waitlist tab
	 * @deprecated
	 */
	isWaitlistEnabled?: boolean;

	// backend meta
	smsOriginPhoneNumber?: string;
	importUserAdditionalData?: Record<string, any>;
	customerIoSyncingError?: string;
	excludeFromLeaderboard?: boolean;

	// user meta
	payoutsInitialized?: boolean;
	stripeUpdateWarningCanceledAt?: string;
	isCommunitySidebarTipWasDisabled?: boolean;
	favoriteCommunities?: string[];

	// milestones
	isWelcomeToCommunityChatComplete?: boolean;
	isScanTutorialPassed?: boolean;
	isCreateEventTutorialPassed?: boolean;
	isCheckInTutorialPassed?: boolean;
	isEditProfileTutorialPassed?: boolean;
	isExploreChatroomTutorialPassed?: boolean;
	ui3TutorialCompleted?: boolean;
	ui3TutorialSkipCount?: number;
	isBlastTutorialShown?: boolean;
	isScoreboardPromotionWasShown?: boolean;
}

export class UserDto {
	public id: string;
	public login: string;
	public email?: string;
	public password?: string;
	public roleId: UserRolesEnum;
	public userName: string;
	public isVerified: boolean;
	public isPartner: boolean;
	public isTempUser?: boolean;
	public isBusinessProfile?: boolean;
	public isFundingProfile?: boolean;
	public isCommunityProfile?: boolean;
	public isGlobalCommunity?: boolean;
	public isRecommendedCommunityProfile?: boolean;
	public clientMetadata?: ClientMetadataDto;
	public fullName: string;
	public avatarId?: string;
	public avatar?: FileDto;
	public backgroundId?: string;
	public background?: FileDto;
	public primaryColor?: string;
	public earlyAccessGroupId?: string;
	public profileChatCid?: string;
	public profileChat?: StreamChatDto;
	public weekJournalsNumber?: number;
	public monthJournalsNumber?: number;
	public overallJournalsNumber?: number;
	public monthSweatHeroPlace?: number;
	public communityProfilesCount?: number;

	public subscribers?: UserToFollowerDto[];

	public followers?: UserToFollowerDto[];
	public followersCount?: number;

	public friends?: UserToFollowerDto[];
	public friendsCount?: number;

	public emptyAvatarVariant?: EmptyAvatarVariantEnum;

	public isCurrentUser?: boolean;
	public isFriendToCurrentUser?: boolean;
	public isFollowingCurrentUser?: boolean;
	public isCurrentUserSubscribed?: boolean;
	public outgoingSubscriptionAcceptanceStatusId?: AcceptanceOptionsEnum;
	public incomingSubscriptionAcceptanceStatusId?: AcceptanceOptionsEnum;
	public isCurrentUserBlockedUser?: boolean;
	public isUserBlockedCurrentUser?: boolean;
	public userMemberships?: UserToMembershipDto[];
	public userChallenges?: UserToChallengeDto[];
	public currentUserRelatedMemberships?: UserToMembershipDto[];

	public homeAddressLat?: string;
	public homeAddressLng?: string;
	public homeAddressName?: string;
	public homeAddressCountryId?: string;
	public homeAddressStateId?: string;
	public homeAddressCityId?: string;
	public gymAddressLat?: string;
	public gymAddressLng?: string;
	public gymAddressName?: string;
	public addresses?: UserAddressDto[];
	public dietId?: DietsEnum;
	public role?: UserRoleDto;
	public diet?: DietDto;
	public myBio?: string;
	public interests: InterestToUserDto[];
	public answers: UserAnswerDto[];
	public achievements: AchievementDto[];
	public admins?: UserDto[];
	public events: EventDto[];
	public instagramUrl?: string;
	public facebookUrl?: string;
	public paymentUrl?: string;
	public waiverUrl?: string;
	public createdAt?: Date;
	public updatedAt?: Date;
	public streamChatSyncedAt?: Date;
	public genderId?: GendersEnum | null;
	public treatAsGenderId?: GendersEnum;
	public customGender?: string;
	public locationPrivacyOptionId?: LocationPrivacyOptionsEnum;
	public accountPrivacyOptionId: AccountsPrivacyOptionsEnum;
	public dateOfBirth?: Date;
	public googleEmail?: string;
	public appleEmail?: string;
	public homeAddressCity?: CityDto;
	public deepLink?: string;
	public adminPermissions?: AdminPermissionsDto;

	public lookingForOptions?: LookingForOptionDto[];
	public isReadyToSweatMatch?: boolean;
	public sweatMatchPriority: number;
	public sweatMatchProfileCreatedAt?: Date;
	public pauseSweatMatch?: boolean;
	public hideOwnJournals?: boolean;
	public currentSweatMatchRound?: number;

	// fields for mock data
	public posts?: any[];
	public groups?: any[];

	public deletedAt?: Date;
	public passwordRestorationExpirationDate?: Date;
	public allowPalFinderSearch?: boolean;

	public receiptsEmail?: string;
	public customApplicationFeePercent?: number;
	public isProPlanActive?: boolean;
	public isHostAbsorbsFee?: boolean;
	public stripeCustomerId?: string;
	public stripeConnectedAccountId?: string;
	public stripeConnectedAccountSyncedAt?: Date;
	public stripeConnectedAccountOnboarded?: boolean;
	public stripeConnectedAccountChargesEnabled?: boolean;
	public stripeConnectedAccountPayoutsEnabled?: boolean;
	public stripeConnectedAccountStatus?: StripeConnectedAccountStatusEnum;

	public files?: FileDto[];
	public filesCount?: number;

	public refererUserId?: string;
	public refererUser?: UserDto;
	public referalUsers?: UserDto[];

	public usedTickets?: EventTicketDto[];

	public ticketsCount?: number;
	public usedTicketsCount?: number;

	/**
	 * Order - 0...n
	 *
	 * 0 - the most top
	 */
	public discoverCommunityOrder?: number;
	public discoverCommunityImageId?: string;
	public discoverCommunityImage?: FileDto;

	public streamFeedSyncedAt?: Date;

	public eventsCount?: number;
	public userToMemberships?: UserToMembershipDto[];

	public adminToCommunityProfile?: AdminToCommunityProfileDto;

	public lastMonthRsvpLeaderboardEntryId?: string;
	public lastMonthRevenueLeaderboardEntryId?: string;
	public lastMonthRsvpLeaderboardEntry?: LeaderboardSnapshotDto;
	public lastMonthRevenueLeaderboardEntry?: LeaderboardSnapshotDto;

	public lastLoginAt?: Date;
}

class UpdateUserDtoBase {
	@IsOptional()
	@ValidateNested()
	@Type(() => FileUploadDto)
	@MaxBase64FileSize(20)
	@Base64AllowedMimeTypes(ALLOWED_USER_AVATAR_MIME_TYPES)
	public updatedAvatarImg?: FileUploadDto | null;

	@IsOptional()
	@ValidateNested()
	@Type(() => FileUploadDto)
	@MaxBase64FileSize(20)
	@Base64AllowedMimeTypes(ALLOWED_USER_BG_IMAGE_MIME_TYPES)
	public updatedBackgroundImg?: FileUploadDto | null;

	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => UpdateUserAddressDto)
	public updatedAddresses?: UpdateUserAddressDto[];

	@IsOptional()
	@MinLength(3, { message: 'Address is too short' })
	public homeAddressName?: string;

	@IsOptional()
	@IsEmail()
	public email?: string;

	@IsOptional()
	public interestsToInsert?: AddInterestToUserDto[];

	@IsOptional()
	@IsArray()
	public interestsIdsToDelete?: string[];

	@IsOptional()
	public passwordRestorationExpirationDate?: Date;

	@IsOptional()
	@IsPositive()
	@IsInt()
	public customApplicationFeePercent?: number;

	public isBusinessProfile?: boolean;
	public isFundingProfile?: boolean;
	public gymAddressLat?: string;
	public gymAddressLng?: string;
	public gymAddressName?: string;
	public dietId?: DietsEnum;
	public myBio?: string;
	public instagramUrl?: string;
	public facebookUrl?: string;
	public locationPrivacyOptionId?: LocationPrivacyOptionsEnum;
	public dateOfBirth?: Date;
	public homeAddressLat?: string;
	public homeAddressLng?: string;
	public primaryColor?: string | null;
	public allowPalFinderSearch?: boolean;
	public clientMetadata?: ClientMetadataDto;
	public pauseSweatMatch?: boolean;
	public hideOwnJournals?: boolean;
}

export class UpdateUserDto extends UpdateUserDtoBase {
	@IsOptional()
	@MinLength(3, { message: 'Full name is too short' })
	public fullName?: string;

	@IsOptional()
	@MinLength(3, { message: 'Login is too short' })
	@IsPhoneNumber(undefined, { message: 'Phone number is invalid' })
	public login?: string;

	@IsOptional()
	@MinLength(3)
	@Matches(NO_WHITESPACE_REGEXP, { message: 'Nickname should contain only letters and digits' })
	public userName?: string;

	public isCommunityProfile?: boolean;
	public isGlobalCommunity?: boolean;
	public isRecommendedCommunityProfile?: boolean;

	@IsOptional()
	@IsNotEmpty({ message: 'Privacy option should not be empty' })
	public accountPrivacyOptionId?: AccountsPrivacyOptionsEnum;

	@ValidateNested()
	@Type(() => OtherGenderDto)
	public otherGender?: OtherGenderDto;

	public genderId?: GendersEnum | null;

	public earlyAccessGroupId?: string;

	public isProfileChatAllowed?: boolean;

	public stripeConnectedAccountId?: string;
	public stripeConnectedAccountChargesEnabled?: boolean;
	public stripeConnectedAccountPayoutsEnabled?: boolean;
	public stripeConnectedAccountOnboarded?: boolean;
	public stripeConnectedAccountStatus?: StripeConnectedAccountStatusEnum;

	@ValidateIf(o => o.isProfileChatAllowed)
	@IsNotEmpty({ message: 'Chatroom should not be empty' })
	public profileChatCid?: string;

	@IsOptional()
	@IsUrl({}, { message: '"Payment" link should be a valid URL' })
	public paymentUrl?: string;

	@IsOptional()
	@IsUrl({}, { message: '"Waiver" link should be a valid URL' })
	public waiverUrl?: string;

	@IsOptional()
	@IsNotEmpty({ message: 'Email is required' })
	@IsEmail(undefined, { message: 'Invalid email' })
	public receiptsEmail?: string;

	@IsOptional()
	@IsEmail()
	public googleEmail?: string;

	@IsOptional()
	@IsEmail()
	public appleEmail?: string;

	@IsOptional()
	@IsNumber()
	@Min(0)
	public sweatMatchPriority?: number;

	public isProPlanActive?: boolean;

	public isHostAbsorbsFee?: boolean;

	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => FileUploadDto)
	@ArrayMinSize(0)
	@ArrayMaxSize(10)
	@MaxBase64FileSize(20)
	@Base64AllowedMimeTypes(ALLOWED_SWEAT_MATCH_IMAGE_MIME_TYPES)
	public imagesToUpload?: FileUploadDto[];

	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => FileUploadDto)
	@ArrayMinSize(0)
	@ArrayMaxSize(10)
	@MaxBase64FileSize(100)
	@Base64AllowedMimeTypes(ALLOWED_SWEAT_MATCH_VIDEO_MIME_TYPES)
	public videosToUpload?: FileUploadDto[];

	@IsOptional()
	public fileIdsToDelete?: string[];

	@IsOptional()
	public createdAt?: Date;

	@IsOptional()
	@IsNumber()
	public changeDiscoverCommunityOrder?: number;

	@IsOptional()
	@IsNumber()
	public discoverCommunityOrder?: number | null;

	@IsOptional()
	@ValidateNested()
	@Type(() => FileUploadDto)
	@MaxBase64FileSize(20)
	@Base64AllowedMimeTypes(ALLOWED_USER_BG_IMAGE_MIME_TYPES)
	public updatedDiscoverCommunityImg?: FileUploadDto | null;

	public adminsIdsToInsert?: string[];
	public adminsIdsToDelete?: string[];

	public refererUserId?: string;
	public referalCode?: string;
	public useRefererCode?: string;

	public isTempUser?: boolean;

	public static fromUser (user: UserDto): UpdateUserDto {
		const updateUserDto = new UpdateUserDto();

		updateUserDto.userName = user.userName;
		updateUserDto.isCommunityProfile = user.isCommunityProfile;
		updateUserDto.isGlobalCommunity = user.isGlobalCommunity;
		updateUserDto.isBusinessProfile = user.isBusinessProfile;
		updateUserDto.isFundingProfile = user.isFundingProfile;
		updateUserDto.fullName = user.fullName;
		updateUserDto.primaryColor = user.primaryColor;
		updateUserDto.homeAddressName = user.homeAddressName;
		updateUserDto.homeAddressLat = user.homeAddressLat;
		updateUserDto.homeAddressLng = user.homeAddressLng;
		updateUserDto.gymAddressLat = user.gymAddressLat;
		updateUserDto.gymAddressLng = user.gymAddressLng;
		updateUserDto.gymAddressName = user.gymAddressName;
		updateUserDto.dietId = user.dietId;
		updateUserDto.myBio = user.myBio;
		updateUserDto.instagramUrl = user.instagramUrl;
		updateUserDto.facebookUrl = user.facebookUrl;
		updateUserDto.paymentUrl = user.paymentUrl;
		updateUserDto.waiverUrl = user.waiverUrl;
		updateUserDto.genderId = user.genderId;
		updateUserDto.earlyAccessGroupId = user.earlyAccessGroupId;
		updateUserDto.locationPrivacyOptionId = user.locationPrivacyOptionId;
		updateUserDto.accountPrivacyOptionId = user.accountPrivacyOptionId;
		updateUserDto.dateOfBirth = user.dateOfBirth;
		updateUserDto.login = user.login;
		updateUserDto.profileChatCid = user.profileChatCid;
		updateUserDto.isProfileChatAllowed = !!user.profileChatCid;
		updateUserDto.clientMetadata = user.clientMetadata ?? {};
		updateUserDto.pauseSweatMatch = user.pauseSweatMatch;
		updateUserDto.hideOwnJournals = user.hideOwnJournals;
		updateUserDto.receiptsEmail = user.receiptsEmail;
		updateUserDto.sweatMatchPriority = user.sweatMatchPriority;
		updateUserDto.isRecommendedCommunityProfile = user.isRecommendedCommunityProfile;
		updateUserDto.discoverCommunityOrder = user.discoverCommunityOrder;
		updateUserDto.createdAt = user.createdAt;
		updateUserDto.customApplicationFeePercent = user.customApplicationFeePercent;
		updateUserDto.isProPlanActive = user.isProPlanActive;
		updateUserDto.isHostAbsorbsFee = user.isHostAbsorbsFee;

		if (user.customGender && user.treatAsGenderId) {
			updateUserDto.otherGender = new OtherGenderDto();
			updateUserDto.otherGender.treatAsGenderId = user.treatAsGenderId;
			updateUserDto.otherGender.name = user.customGender;
		}

		return updateUserDto;
	}
}

export class UpdateUserAdminDto extends UpdateUserDtoBase {
	@IsOptional()
	@IsNotEmpty()
	@MinLength(3, { message: 'Full name is too short' })
	public fullName?: string;

	@IsOptional()
	@ValidateIf(o => o.login?.length > 0)
	@MinLength(3, { message: 'Login is too short' })
	@IsPhoneNumber(undefined, { message: 'Phone number is invalid' })
	public login?: string;

	@IsOptional()
	@IsNotEmpty()
	@MinLength(3)
	@Matches(NO_WHITESPACE_REGEXP, { message: 'Nickname should contain only letters and digits' })
	public userName?: string;

	@IsOptional()
	@IsNotEmpty({ message: 'Privacy option should not be empty' })
	public accountPrivacyOptionId?: AccountsPrivacyOptionsEnum;

	@IsOptional()
	@ValidateIf(o => isEqual(o.genderId, 'other'))
	@IsNotEmpty({ message: 'Other gender should not be empty' })
	@ValidateNested({ message: 'Other gender should not be empty' })
	@Type(() => OtherGenderDto)
	public otherGender?: OtherGenderDto;

	@IsOptional()
	@IsUrl({}, { message: '"Payment" link should be a valid URL' })
	public paymentUrl?: string;

	@IsOptional()
	@IsUrl({}, { message: '"Waiver" link should be a valid URL' })
	public waiverUrl?: string;

	@IsOptional()
	public isRecommendedCommunityProfile?: boolean;

	public genderId?: GendersEnum | null;

	public adminsIdsToInsert?: string[];
	public adminsIdsToDelete?: string[];

	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => FileUploadDto)
	@ArrayMinSize(0)
	@ArrayMaxSize(10)
	@MaxBase64FileSize(20)
	@Base64AllowedMimeTypes(ALLOWED_SWEAT_MATCH_IMAGE_MIME_TYPES)
	public imagesToUpload?: FileUploadDto[];

	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => FileUploadDto)
	@ArrayMinSize(0)
	@ArrayMaxSize(10)
	@MaxBase64FileSize(100)
	@Base64AllowedMimeTypes(ALLOWED_SWEAT_MATCH_VIDEO_MIME_TYPES)
	public videosToUpload?: FileUploadDto[];

	@IsOptional()
	public fileIdsToDelete?: string[];

	/**
	 * Move community up - decrease order = - 1
	 *
	 * Move community down - increase order = + 1
	 */
	@IsOptional()
	@IsNumber()
	public changeDiscoverCommunityOrder?: number;

	@IsOptional()
	@IsNumber()
	public discoverCommunityOrder?: number | null;

	@IsOptional()
	@ValidateNested()
	@Type(() => FileUploadDto)
	@MaxBase64FileSize(20)
	@Base64AllowedMimeTypes(ALLOWED_USER_BG_IMAGE_MIME_TYPES)
	public updatedDiscoverCommunityImg?: FileUploadDto | null;
}

export type CommunityProfileOnboardingDto = Pick<
	UpdateUserDto,
	| 'userName'
	| 'fullName'
	| 'myBio'
	| 'receiptsEmail'
	| 'updatedAvatarImg'
	| 'updatedBackgroundImg'
	| 'interestsToInsert'
	| 'homeAddressName'
	| 'homeAddressLat'
	| 'homeAddressLng'
	| 'accountPrivacyOptionId'
	| 'locationPrivacyOptionId'
	| 'imagesToUpload'
	| 'videosToUpload'
	| 'primaryColor'
	| 'updatedAddresses'
>;

export interface ValidateUserDto {
	user?: UserDto;
	error?: boolean;
	message?: string;
}

export type UserParticipantShortDto = Pick<UserDto, 'id' | 'userName' | 'fullName' | 'email' | 'receiptsEmail'>;
export type UserParticipantShortDtoWithIntakeFromData = UserParticipantShortDto & {
	intakeFormResults?: IntakeFormUserDataResults;
	acceptanceOptionId?: EventAttendanceOptionsEnum;
	prices?: EventPriceDto[];

	price?: EventPriceDto;
};

export type NewsletterDto = {
	id: string;
	email: string;
	createdAt: Date;
};
